/**
 * OIMS v2 - Authentication module
 * Token management, login/logout, session expiry, axios interceptor
 */

import { COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI, COGNITO_SCOPES, API_BASE } from './config.js';

let sessionExpiryTimer = null;
let isLoggingOut = false;

export function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

export function isTokenExpired(token) {
  const decoded = decodeJwt(token);
  if (!decoded || !decoded.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return decoded.exp <= now;
}

export function doLogin() {
  const loginUrl = `${COGNITO_DOMAIN}/login?client_id=${encodeURIComponent(COGNITO_CLIENT_ID)}&response_type=token&scope=${encodeURIComponent(COGNITO_SCOPES)}&redirect_uri=${encodeURIComponent(COGNITO_REDIRECT_URI)}`;
  window.location.href = loginUrl;
}

export async function logout(state, expired = false) {
  if (isLoggingOut) return;
  isLoggingOut = true;

  if (!expired && state.accessToken) {
    try {
      await axios.post("/auth/logout");
    } catch (e) {
      console.error("Failed to log logout:", e);
    }
  }

  state.accessToken = null;
  localStorage.removeItem("access_token");
  localStorage.removeItem("login_logged");
  localStorage.removeItem("oims_display_name");
  // H-4: Clear notification cache from sessionStorage
  sessionStorage.removeItem("oims_notifications");
  if (sessionExpiryTimer) {
    clearTimeout(sessionExpiryTimer);
    sessionExpiryTimer = null;
  }
  updateAuthUI(state);

  const badge = document.getElementById("user-info");
  if (expired && badge) {
    badge.textContent = "Session expired";
  }

  setTimeout(() => {
    isLoggingOut = false;
    // Redirect to Cognito logout to terminate the Cognito session cookie,
    // then Cognito redirects back to our login page.
    const cognitoLogout = `${COGNITO_DOMAIN}/logout?client_id=${encodeURIComponent(COGNITO_CLIENT_ID)}&logout_uri=${encodeURIComponent(window.location.origin + "/logout")}`;
    window.location.href = cognitoLogout;
  }, 500);
}

export async function parseTokenFromHash(state) {
  if (window.location.hash && window.location.hash.startsWith("#")) {
    const hash = new URLSearchParams(window.location.hash.substring(1));
    const at = hash.get("access_token");
    if (at) {
      state.accessToken = at;
      localStorage.setItem("access_token", at);
      localStorage.removeItem("login_logged");

      // Capture id_token for name attributes (access tokens don't carry them)
      const idToken = hash.get("id_token");
      if (idToken) {
        const idClaims = decodeJwt(idToken);
        if (idClaims) {
          if (idClaims.name) localStorage.setItem("oims_display_name", idClaims.name);
          else if (idClaims.given_name) {
            const dn = idClaims.family_name ? `${idClaims.given_name} ${idClaims.family_name}` : idClaims.given_name;
            localStorage.setItem("oims_display_name", dn);
          }
        }
      }

      window.history.replaceState({}, document.title, window.location.pathname);

      try {
        const { data } = await axios.post("/auth/session");
        localStorage.setItem("login_logged", "true");
        if (data.display_name) localStorage.setItem("oims_display_name", data.display_name);
      } catch (e) {
        console.error("Failed to log login:", e);
      }
    }
  }
}

export async function logSessionIfNeeded(state) {
  if (!state.accessToken) return;
  const loginLogged = localStorage.getItem("login_logged");
  if (!loginLogged) {
    try {
      const { data } = await axios.post("/auth/session");
      localStorage.setItem("login_logged", "true");
      if (data.display_name) localStorage.setItem("oims_display_name", data.display_name);
    } catch (e) {
      console.error("Failed to log session:", e);
    }
  }
}

export function loadToken(state) {
  const at = localStorage.getItem("access_token");
  if (at) state.accessToken = at;
}

export function updateAuthUI(state) {
  const badge = document.getElementById("user-info");
  const btnLogout = document.getElementById("btnLogout");
  const btnExecute = document.getElementById("btnExecute");

  if (state.accessToken) {
    if (isTokenExpired(state.accessToken)) {
      logout(state, true);
      return;
    }
    const decoded = decodeJwt(state.accessToken);
    const email = (decoded && (decoded.email || decoded.username || decoded["cognito:username"])) || "";
    const storedName = localStorage.getItem("oims_display_name");
    const displayName = storedName || (email.split("@")[0] || `Token: ${state.accessToken.substring(0, 12)}...`);
    if (badge) badge.textContent = displayName;
    if (btnLogout) btnLogout.disabled = false;
    if (btnExecute) btnExecute.disabled = false;
    scheduleSessionExpiryWatcher(state);
  } else {
    if (badge) badge.textContent = "Not signed in";
    if (btnLogout) btnLogout.disabled = true;
    if (btnExecute) btnExecute.disabled = true;
  }
}

function scheduleSessionExpiryWatcher(state) {
  if (!state.accessToken) return;
  const decoded = decodeJwt(state.accessToken);
  if (!decoded || !decoded.exp) return;

  const nowMs = Date.now();
  const expMs = decoded.exp * 1000;
  const delta = expMs - nowMs;

  if (sessionExpiryTimer) clearTimeout(sessionExpiryTimer);

  if (delta <= 0) {
    handleSessionExpired(state);
  } else {
    sessionExpiryTimer = setTimeout(() => handleSessionExpired(state), delta + 500);
  }
}

function handleSessionExpired(state) {
  if (window.toast) window.toast.warning("Session expired. Redirecting to login...", { duration: 2000 });
  // Clear local state only — do NOT call logout() which races with doLogin
  state.accessToken = null;
  localStorage.removeItem("access_token");
  localStorage.removeItem("login_logged");
  sessionStorage.removeItem("oims_notifications");
  if (sessionExpiryTimer) {
    clearTimeout(sessionExpiryTimer);
    sessionExpiryTimer = null;
  }
  updateAuthUI(state);
  // Redirect to Cognito logout to clear stale session cookie,
  // then Cognito redirects to /logout which redirects to /login
  setTimeout(() => {
    const cognitoLogout = `${COGNITO_DOMAIN}/logout?client_id=${encodeURIComponent(COGNITO_CLIENT_ID)}&logout_uri=${encodeURIComponent(window.location.origin + "/logout")}`;
    window.location.href = cognitoLogout;
  }, 1500);
}

export function ensureLoggedIn() {
  const at = localStorage.getItem("access_token");
  if (!at) {
    window.location.href = "/login";
    return;
  }
}

export function showTokenModal(state, loadActions, loadInstances) {
  const modalEl = document.getElementById("tokenModal");
  const modal = new bootstrap.Modal(modalEl);
  modal.show();

  document.getElementById("btnSaveToken").onclick = function () {
    const val = document.getElementById("manualToken").value.trim();
    if (val) {
      state.accessToken = val;
      localStorage.setItem("access_token", val);
      updateAuthUI(state);
      loadActions();
      loadInstances();
      modal.hide();
    }
  };
}

export function initAuth(state) {
  // Axios defaults
  axios.defaults.baseURL = API_BASE;
  axios.interceptors.request.use(config => {
    if (state.accessToken) {
      config.headers["Authorization"] = `Bearer ${state.accessToken}`;
    }
    return config;
  });

  // Intercept 401s
  axios.interceptors.response.use(
    (resp) => resp,
    (error) => {
      if (error?.response?.status === 401 && !isLoggingOut) {
        handleSessionExpired(state);
      }
      return Promise.reject(error);
    }
  );

  // Session guard
  ensureLoggedIn();
}
